<template>
	<UserRegistrationLayout v-if="!isInRunner" class="login-page">
		<AppLoader v-if="showLoader" />
		<template v-else>
			<div class="slot-title">Welcome back</div>
			<v-form v-model="isFormValid" ref="loginForm" @submit.prevent="login" class="user-input-box">
				<AnemicButton class="google-login-button mt-9 mb-5" @click="signInWithGoogle">
					<SIcon name="google-logo" class="mr-2" />
					Continue with Google
				</AnemicButton>

				<div class="display-flex width-100 flex-row flex-align-center px-5 mb-5">
					<div class="divider width-100"></div>
					<div class="divider-text mx-2">or</div>
					<div class="divider width-100"></div>
				</div>

				<SignupTextField
					label="Email"
					iconName="email"
					:rules="emailRules"
					v-model="userEmail"
					type="email"
					data-cy="registration-username"
					autofocus
					single-line
					placeholder="Enter your email"
				>
				</SignupTextField>
				<SignupTextField
					type="password"
					label="Password"
					iconName="input-key"
					:rules="requiredRules"
					v-model="userPassword"
					placeholder="Enter password"
					data-cy="registration-password"
				>
					<h5 @click="navigateTo({ name: 'Forgot', params: { origEmail: userEmail } })" class="forgot-password reg">Forgot password?</h5>
				</SignupTextField>
				<AnemicButton :loading="submittingForm" class="login-button width-100" type="submit" data-cy="registration-continue">
					Sign in
				</AnemicButton>

				<AnemicButton :loading="ssoLoginLoading" @click="ssoLogin" class="login-button-sso width-100 mt-5" type="button">
					Sign in with SSO
				</AnemicButton>
				<div v-if="errorMessage" class="error-message mt-4">{{ errorMessage }}</div>
			</v-form>
		</template>
		<template #bottom v-if="!showLoader">
			<div class="bottom-section mt-5">
				Don't have an account?
				<a @click="navigateTo({ name: 'Signup' })" class="link-text underline">Get started</a>
			</div>
		</template>
	</UserRegistrationLayout>
</template>

<script>
import api from '@/api';
import { mapActions } from 'vuex';
import credentials from '@/credentials';
import IframeService from '@/utils/iframe-service';
import UserRegistrationLayout from './userRegistration/UserRegistrationLayout';
import SignupTextField from './userRegistration/components/SignupTextField';

import UserRegistrationService from './userRegistration/helpers/UserRegistrationService';
import UserRegistrationMixin from './userRegistration/helpers/UserRegistrationMixin';
import isElectron from './userRegistration/helpers/isElectron';
import ExtensionCommunicationService from '@/utils/extension-communication-service';

const GET_TOKEN_FROM_EXT_KEY = 'get-token';
const SET_TOKEN_FROM_EXT_KEY = 'set-credentials';

export default {
	name: 'Login',
	props: {
		token: { type: String },
		refresh: { type: String }, //from iframe input data
	},
	mixins: [UserRegistrationMixin],
	components: {
		UserRegistrationLayout,
		SignupTextField,
	},
	data: () => ({
		ssoLoginLoading: false,
		auth2: null,
		stage: 1,
		fakeUser: {
			acceptPrivacy: false,
		},
		userEmail: '',
		hideForm: true,
		userPassword: '',
		submittingForm: false,
		requiredRules: UserRegistrationService.getRequiredRules(),
		emailRules: UserRegistrationService.getEmailValidationRules(),
		isFormValid: false,
		errorMessage: '',
		bindLocalCredentialsHandler: null,
	}),
	computed: {
		isInRunner() {
			return isElectron();
		},
		showLoader() {
			return this.hideForm;
		},
	},
	methods: {
		...mapActions(['loginWithUserPass', 'loginWithGoogleToken', 'loginWithToken']),
		async ssoLogin() {
			this.errorMessage = '';
			if (this.userEmail.length === 0) {
				this.errorMessage = 'Please enter your email';
				return;
			}
			this.ssoLoginLoading = true;
			const r = await api.loginWithSSO(this.userEmail).catch((err) => {
				console.log(err);
				this.ssoLoginLoading = false;
				if (err.response?.data?.error?.msg) {
					this.errorMessage = err.response.data.error.msg;
				}
			});
			this.ssoLoginLoading = false;
			if (r.url) {
				window.location.href = r.url;
			}
		},
		async login() {
			this.errorMessage = '';
			this.$refs.loginForm.validate();
			if (this.isFormValid && this.userEmail) {
				this.submittingForm = true;
				this.errorMessage = await this.loginWithUserPass({
					username: this.userEmail,
					password: this.userPassword,
				}).finally(() => {
					this.submittingForm = false;
				});
			}
		},
		async signInWithGoogle() {
			const idToken = await UserRegistrationService.signInWithGoogle();
			const loginRes = await this.loginWithGoogleToken({ token: idToken }).catch((err) => {
				this.$toast.error(err, { timeout: 3000 });
			});
			if (loginRes.success === false) {
				this.errorMessage = 'Login failed. Please sign up.';
			}
		},
		async loadCredentialsHandler(event) {
			const { data } = event;
			if (data.action === SET_TOKEN_FROM_EXT_KEY) {
				const { user, token, refresh, account } = data.payload;
				credentials.set({ user, token, refresh, account });
				try {
					await this.loginWithToken(token);
				} catch {
					this.hideForm = false;
				}
			}
		},
	},
	async created() {
		this.hideForm = true;
		const token = credentials.token || this.token;
		const isRouterGuardTokenLoginSupported = await ExtensionCommunicationService.doesSupportsRouterGuardTokenLogin();
		if (token) {
			try {
				await this.loginWithToken(token);
			} catch {
				this.hideForm = false;
			}
		} else if (IframeService.inIframe() && !isRouterGuardTokenLoginSupported) {
			// doesSupportsRouterGuardTokenLogin => support for backwards compatibility. do not remove this code block, as long as we have users using the old extension
			ExtensionCommunicationService.notifyExtensionPageCreated();
			this.bindLocalCredentialsHandler = this.loadCredentialsHandler.bind(this);
			IframeService.addListeners(this.bindLocalCredentialsHandler);
			IframeService.sendMessageToParent(GET_TOKEN_FROM_EXT_KEY);

			setTimeout(() => {
				this.hideForm = false; //fallback to stop the loader
			}, 3500);
		} else {
			this.hideForm = false;
		}
	},
	mounted() {
		UserRegistrationService.initializeGoogleAuth();
	},
	beforeDestroy() {
		this.bindLocalCredentialsHandler && IframeService.removeListener(this.bindLocalCredentialsHandler);
	},
};
</script>

<style scoped lang="scss">
.bottom-section {
	font-size: 14px;
	.link-text {
		cursor: pointer;
		text-decoration: underline;
		color: var(--primary-color);
	}
}

.google-login-button {
	padding: 0px;
	width: 340px;
	height: 45px;
	background: var(--bold-border-color) !important;
	text-transform: none !important;

	color: var(--bg-color);
	font-size: 14px !important;
}
.divider-text {
	font-size: 14px;
	color: #babdc4;
}
.divider {
	height: 1px;
	margin-top: 2px;
	background: #babdc4;
}

.login-button {
	height: 45px;
	padding: 0px;
	font-size: 14px !important;
	color: var(--bold-border-color);
	background: var(--bg-color) !important;
	border: 1px solid var(--bold-border-color);
	text-transform: none !important;
}

.login-button-sso {
	height: 45px;
	padding: 0px;
	font-size: 14px !important;
	background: #fff !important;
	color: var(--bold-border-color);
	text-transform: none !important;
}

.error-message {
	max-width: 340px;
	color: red;
}
.forgot-password {
	color: var(--bold-border-color);
}
h5 {
	font-size: 13px;
}
</style>
